import MeasurementVolume from "../measurement/MeasurementVolume";
import DitapPopup from "./DitapPopup";
import DitapAlert from "./DitapAlert";
import Utils from "../basic/Utils";
import DitapMobileSelectPopup from "./DitapMobileSelectPopup";

/**
 * 체적량 측정 팝업 클래스. </br>
 * MeasurementVolume popup class.
 * @alias PopupMeasurementVolume
 * @extends DitapPopup
 *
 * @constructor
 * @param {MeasurementVolume} measurementVolume 체적량 측정 연산 및 영역 표시 클래스. </br> Volume Measurement Calculation and Area Display Class.
 *
 * @example
 * const getUrlFromBase64 = (base64Data) => {
 *   const b64Data = base64Data;
 *   const byteCharacters = window.atob(b64Data);
 *   const byteNumbers = new Array(byteCharacters.length);
 *   for (let i = 0; i < byteCharacters.length; i++) {
 *     byteNumbers[i] = byteCharacters.charCodeAt(i);
 *   }
 *   const byteArray = new Uint8Array(byteNumbers);
 *   const blob = new Blob([byteArray], { type: "image/png" });
 *
 *   return URL.createObjectURL(blob);
 * };
 *
 * const viewer = new Ditap.DitapViewer("ditapContainer", {
 *   baseLayerPicker: true,
 *   // 지형(터레인 지정)
 *   terrainProviderViewModels: [
 *     new Ditap.ProviderViewModel({
 *       name: "Ditap Korea Terrain",
 *       iconUrl: getUrlFromBase64(
 *         Ditap.ConstantUrl.DITAP_TERRAIN_IMG_URL
 *       ),
 *       tooltip: "WGS84 standard ellipsoid, also known as EPSG:4326",
 *       category: "Ditap",
 *       creationFunction: function () {
 *         return new Ditap.CesiumTerrainProvider({
 *           url: "http://121.135.139.45:9092/tilesets/layer",
 *         });
 *       },
 *     }),
 *   ],
 * });
 *
 * const destination = Ditap.Cartesian3.fromDegrees(
 *   126.978275264,
 *   37.566642192,
 *   400
 * );
 *
 * // 카메라 이동
 * viewer.scene.camera.flyTo({
 *   destination: destination,
 * });
 *
 * const positions = [
 *   new Ditap.Cartesian3(-3044739.446567739, 4043823.403143691,3867463.012077135),
 *   new Ditap.Cartesian3(-3044747.714411474, 4043814.3380325786,3867466.015981707),
 *   new Ditap.Cartesian3(-3044752.780860346, 4043817.3342751344,3867458.802892699),
 * ];
 * const props = {
 *   positions: positions,
 *   callback: (measurementVolume) => {
 *     // 콜백 함수를 이용하여 계산이 완료된 MeasurementVolume을 할당
 *     const popupMeasurementVolume = new Ditap.PopupMeasurementVolume(measurementVolume);
 *     popupMeasurementVolume.open();
 *   }
 * };
 * const measurementVolume = new Ditap.MeasurementVolume(viewer, props);
 */
class PopupMeasurementVolume extends DitapPopup {
  constructor(measurementVolume, ditapPaneManager) {
    // 팝업 부모 지정
    const parent = measurementVolume?.viewer?.container;

    super(parent, ditapPaneManager);

    // 뷰어, 핸들러
    if (measurementVolume instanceof MeasurementVolume) {
      this.measurementVolume = measurementVolume;
      this.id = `${measurementVolume.id}_Popup`;
    } else {
      throw Error("PopupMeasurementVolume measurementVolume type error");
    }
    //this.viewer = measurementVolume.viewer;
    //this.handler = new ScreenSpaceEventHandler(this.viewer.canvas);

    // 모바일 셀렉트 박스 생성
    this.mobileHeightSelectPopup = new DitapMobileSelectPopup(parent);
    this.mobileHeightSelectPopup.addTitle("기준 밑면");
    this.mobileHeightSelectPopup.closeCallback = () => {
      this.planHeightPopup.classList.remove("on");
      this.planHeightList.classList.add("hide");
      // if (this.planHeightList.classList.contains("hide")) {
      //   this.closePlanHeightListIcon();
      // }
    };

    // 팝업 생성
    const infoBox = this.createInfoBox();
    // 헤더 추가
    this.prependHeader(infoBox["header"]["title"]);
    // 컨텐츠 추가
    this.appendSubTitle(infoBox["content"]["subTitle"]);
    this.appendContent(infoBox["content"]["mainWrapper"]);
    this.appendContent(infoBox["content"]["messageWrapper"]);
    this.appendContent(infoBox["content"]["planHeightWrapper"]);
    this.appendContent(infoBox["content"]["detailWrapper"]);
    // 팝업 설정
    this.footer.remove();

    this.changeCloseBtn();
    const isMobile = Utils.isBodyWidthLessThan600();
    if (!isMobile) {
      this.enableDrag();
      this.enableTouch();
    }

    // 노드 세팅
    this._mainCuttingVolume = infoBox["content"]["mainCuttingVolume"];
    this._mainFillingVolume = infoBox["content"]["mainFillingVolume"];
    this._planHeightPopup = infoBox["content"]["planHeightPopup"];
    this._planHeightSelectDropImage =
      infoBox["content"]["planHeightSelectDropImage"];
    this._planHeightSelectText = infoBox["content"]["planHeightSelectText"];
    this._planHeightList = infoBox["content"]["planHeightList"];
    this._planHeightItem = infoBox["content"]["planHeightItem"];
    this._planHeightInputPopup = infoBox["content"]["planHeightInputPopup"];
    this.planHeightInputBtnWrapper =
      infoBox["content"]["planHeightInputBtnWrapper"];
    this._planHeight = infoBox["content"]["planHeight"];
    this._planHeightSearch = infoBox["content"]["planHeightSearch"];
    this._detailCuttingVolume = infoBox["content"]["detailCuttingVolume"];
    this._detailFillingVolume = infoBox["content"]["detailFillingVolume"];
    this._detailTotalVolume = infoBox["content"]["detailTotalVolume"];
    this._messageWrapper = infoBox["content"]["messageWrapper"];

    // 경고창 생성
    this._createWarningAlert = new DitapAlert({
      type: "info",
      title: "정보",
      content1: "체적 계산 중입니다.",
      content2: "계산이 끝난 후 시도해 주세요.",
      buttons: "ONLYOK",
      open: false,
      callback: () => {},
    });

    // 이벤트 바인딩
    this.setEvent();
  }

  /**
   * 체적량 측정 인스턴스. </br>
   * MeasurementVolume instance.
   * @readonly
   * @type {MeasurementVolume}
   */
  get measurementVolume() {
    return this._measurementVolume;
  }
  set measurementVolume(measurementVolume) {
    this._measurementVolume = measurementVolume;
  }

  /**
   * 메인 절토량 요소. </br>
   * Main cutting volume.
   * @readonly
   * @type {Element}
   */
  get mainCuttingVolume() {
    return this._mainCuttingVolume;
  }
  set mainCuttingVolume(mainCuttingVolume) {
    this._mainCuttingVolume = mainCuttingVolume;
  }

  /**
   * 메인 성토량 요소. </br>
   * Main fill volume.
   * @readonly
   * @type {Element}
   */
  get mainFillingVolume() {
    return this._mainFillingVolume;
  }
  set mainFillingVolume(mainFillingVolume) {
    this._mainFillingVolume = mainFillingVolume;
  }

  /**
   * 계획고 영역 요소. </br>
   * Plan height wrapper.
   * @readonly
   * @type {Element}
   */
  get planHeightPopup() {
    return this._planHeightPopup;
  }
  set planHeightPopup(planHeightPopup) {
    this._planHeightPopup = planHeightPopup;
  }

  get planHeightSelectDropImage() {
    return this._planHeightSelectDropImage;
  }
  set planHeightSelectDropImage(planHeightSelectDropImage) {
    this._planHeightSelectDropImage = planHeightSelectDropImage;
  }

  /**
   * 선택된 계획고 유형 요소. </br>
   * Selected plan height type.
   * @readonly
   * @type {Element}
   */
  get planHeightSelectText() {
    return this._planHeightSelectText;
  }
  set planHeightSelectText(planHeightSelectText) {
    this._planHeightSelectText = planHeightSelectText;
  }

  /**
   * 계획고 유형 목록 요소. </br>
   * Plan height type list.
   * @readonly
   * @type {Element}
   */
  get planHeightList() {
    return this._planHeightList;
  }
  set planHeightList(planHeightList) {
    this._planHeightList = planHeightList;
  }

  /**
   * 계획고 유형 목록 아이템 요소. </br>
   * Plan height type items.
   * @readonly
   * @type {Element}
   */
  get planHeightItem() {
    return this._planHeightItem;
  }
  set planHeightItem(planHeightItem) {
    this._planHeightItem = planHeightItem;
  }

  /**
   * 계획고 입력 영역 요소. </br>
   * Plan height input wrapper.
   * @readonly
   * @type {Element}
   */
  get planHeightInputPopup() {
    return this._planHeightInputPopup;
  }
  set planHeightInputPopup(planHeightInputPopup) {
    this._planHeightInputPopup = planHeightInputPopup;
  }

  /**
   * 계획고 입력 요소. </br>
   * Plan height input.
   * @readonly
   * @type {Element}
   */
  get planHeight() {
    return this._planHeight;
  }
  set planHeight(planHeight) {
    this._planHeight = planHeight;
  }

  /**
   * 계획고 검색 요소. </br>
   * Plan height search button.
   * @readonly
   * @type {Element}
   */
  get planHeightSearch() {
    return this._planHeightSearch;
  }
  set planHeightSearch(planHeightSearch) {
    this._planHeightSearch = planHeightSearch;
  }

  /**
   * 절토량 상세정보 요소. </br>
   * Cutting volume details.
   * @readonly
   * @type {Element}
   */
  get detailCuttingVolume() {
    return this._detailCuttingVolume;
  }
  set detailCuttingVolume(detailCuttingVolume) {
    this._detailCuttingVolume = detailCuttingVolume;
  }

  /**
   * 성토량 상세정보 요소. </br>
   * Fill volume details.
   * @readonly
   * @type {Element}
   */
  get detailFillingVolume() {
    return this._detailFillingVolume;
  }
  set detailFillingVolume(detailFillingVolume) {
    this._detailFillingVolume = detailFillingVolume;
  }

  /**
   * 절토량과 성토량의 합 요소. </br>
   * Sum of fill volume and cutting volume.
   * @readonly
   * @type {Element}
   */
  get detailTotalVolume() {
    return this._detailTotalVolume;
  }
  set detailTotalVolume(detailTotalVolume) {
    this._detailTotalVolume = detailTotalVolume;
  }

  /**
   * 경고 알림 요소. </br>
   * Warning alert element.
   * @readonly
   * @type {Element}
   */
  get createWarningAlert() {
    return this._createWarningAlert;
  }
  set createWarningAlert(createWarningAlert) {
    this._createWarningAlert = createWarningAlert;
  }

  setMobilePopup() {
    const container = this.planHeightPopup;
    const popup = this.mobileHeightSelectPopup;
    const content = this.planHeightList;
    const isMobile = Utils.isBodyWidthLessThan600();
    if (isMobile && container.contains(content)) {
      container.removeChild(content);
      popup.appendContent(content);
    } else if (!isMobile && popup.content.contains(content)) {
      popup.content.removeChild(content);
      container.appendChild(content);
    }
  }

  /**
   * 계획고 유형 목록 아이콘을 열림 상태로 변경. </br>
   * Change the icon of the planning type list to the open state.
   */
  openPlanHeightListIcon() {
    if (this.planHeightPopup instanceof Element) {
      this.planHeightPopup.classList.add("on");
      this.mobileHeightSelectPopup.open();
    }
    this.setMobilePopup();
  }

  /**
   * 계획고 유형 목록 아이콘을 닫기 상태로 변경. </br>
   * Change the icon of the planning type list to the close state.
   */
  closePlanHeightListIcon() {
    if (this.planHeightPopup instanceof Element) {
      this.planHeightPopup.classList.remove("on");
      this.mobileHeightSelectPopup.close();
    }
    this.setMobilePopup();
  }

  /**
   * 키보드 이벤트. </br>
   * Keyboard input event
   */
  async handleKeyDown(e) {
    if (e.key === "Enter") {
      if (
        !this.planHeightInputPopup.classList.contains("hide") &&
        !this.wrapper.classList.contains("hide")
      ) {
        const value = Number(this.planHeight.value);
        if (!isNaN(value)) {
          await this.measurementVolume.calculateVolume({
            positions: this.measurementVolume.positions,
            planHeight: value,
            cellSize: this.measurementVolume.cellSize,
            showCell: this.measurementVolume.showCell,
            fillingVolumeColor: this.measurementVolume.fillingVolumeColor,
            cuttingVolumeColor: this.measurementVolume.cuttingVolumeColor,
            classification: this.measurementVolume.classification,
            loading: this.measurementVolume.loading,
            callback: this.measurementVolume.callback,
          });
          this.update();
        }
      }
    }
  }

  /**
   * 팝업 업데이트. </br>
   * Update popup
   */
  update() {
    if (!(this.measurementVolume instanceof MeasurementVolume)) {
      return;
    }

    const volume = this.measurementVolume;
    this.setMainCuttingVolume(volume.cuttingVolume); // 메인 절토량
    this.setMainFillingVolume(volume.fillingVolume); // 메인 성토량
    this.setdetailCuttingVolume(volume.cuttingVolume); // 상세정보 절토량
    this.setdetailFillingVolume(volume.fillingVolume); // 상세정보 성토량
    this.setdetailTotalVolume(volume.totalVolume); // 상세정보 총합
    this.setplanHeight(volume.planHeight); // 계획고
    this.setplanHeightSelectText(volume.planHeightType); // 기준 밑면 타입

    // 안내 문구 표시할지 여부.
    if (volume.isMessage) {
      this._messageWrapper.classList.remove("hide");
    } else {
      this._messageWrapper.classList.add("hide");
    }
  }

  /**
   * 메인 절토량 설정. </br>
   * Set main cutting volume value.
   * @param {number} value 절토량 (m³).</br> Main cutting volume (m³).
   */
  setMainCuttingVolume(value) {
    value = Number(value);
    if (!isNaN(value)) {
      if (value === 0) {
        value = 0;
      } else {
        value = value.toFixed(2);
      }
    } else {
      value = 0;
    }
    this.mainCuttingVolume.textContent = value;
  }

  /**
   * 메인 성토량 설정. </br>
   * Set main fill volume value.
   * @param {number} value 성토량 (m³).</br> Main fill volume (m³).
   */
  setMainFillingVolume(value) {
    value = Number(value);
    if (!isNaN(value)) {
      if (value === 0) {
        value = 0;
      } else {
        value = value.toFixed(2);
      }
    } else {
      value = 0;
    }
    this.mainFillingVolume.textContent = value;
  }

  /**
   * 절토량 상세정보 설정. </br>
   * Set cutting volume details value
   * @param {number} value 절토량 (m³).</br> Detail cutting volume (m³).
   */
  setdetailCuttingVolume(value) {
    value = Number(value);
    if (!isNaN(value)) {
      if (value === 0) {
        value = 0;
      } else {
        value = value.toFixed(2);
      }
    } else {
      value = 0;
    }
    this.detailCuttingVolume.textContent = value;
  }

  /**
   * 성토량 상세정보 설정. </br>
   * Set fill volume details value.
   * @param {number} value 성토량 (m³).</br> Main fill volume (m³).
   */
  setdetailFillingVolume(value) {
    value = Number(value);
    if (!isNaN(value)) {
      if (value === 0) {
        value = 0;
      } else {
        value = value.toFixed(2);
      }
    } else {
      value = 0;
    }
    this.detailFillingVolume.textContent = value;
  }

  /**
   * 절토량과 성토량의 합 설정. </br>
   * Set total volume details value.
   * @param {number} value 절토량과 성토량의 합 (m³).</br> Main total volume (m³).
   */
  setdetailTotalVolume(value) {
    value = Number(value);
    if (!isNaN(value)) {
      if (value === 0) {
        value = 0;
      } else {
        value = value.toFixed(2);
      }
    } else {
      value = 0;
    }
    this.detailTotalVolume.textContent = value;
  }

  /**
   * 계획고 설정. </br>
   * Change the custom plan height text.
   * @param {number} value 계획고 (미터).</br> Plan Height (meters).
   */
  setplanHeight(value) {
    value = Number(value);
    if (isNaN(value)) {
      value = 0;
    }
    this.planHeight.value = value;
  }

  /**
   * 체적량 측정 유형 설정. </br>
   * Change volume measurement type text.
   * @param {string} value 측정 유형 ["min", "max", "average", "user"].</br> Measurement type ["min", "max", "average", "user"].
   */
  setplanHeightSelectText(value) {
    let planeHeightType;
    switch (value) {
      case "min":
        planeHeightType = "최저점 기준";
        break;
      case "max":
        planeHeightType = "최고점 기준";
        break;
      case "average":
        planeHeightType = "평균점 기준";
        break;
      case "user":
        planeHeightType = "사용자 지정 고도";
        break;
      default:
        planeHeightType = "평균점 기준";
    }
    this.planHeightSelectText.textContent = planeHeightType;
  }

  /**
   * 이벤트 설정. </br>
   * Binding popup events.
   */
  setEvent() {
    this.planHeightPopup.addEventListener("click", () => {
      this.toggle(this.planHeightList);
      if (this.planHeightList.classList.contains("hide")) {
        this.closePlanHeightListIcon();
      } else {
        this.openPlanHeightListIcon();
      }
    });

    // 기준 밑면 항목 선택
    this.planHeightItem.forEach((elem) => {
      elem.addEventListener("click", async (e) => {
        e.stopPropagation();

        //셀렉트 목록 숨기기, 활성화 제거.
        this.planHeightList.classList.add("hide");
        this._planHeightPopup.classList.remove("on");
        this.mobileHeightSelectPopup.close();
        // this.close(this.planHeightList);
        // this.open(this.planHeightInputPopup);

        this.planHeightSelectText.textContent = e.target.textContent;

        // 선택한 옵션 값
        const value = e.target.getAttribute("value");
        if (value === "user") {
          //this.open(this.planHeightInputPopup);
          this.planHeightInputPopup.classList.remove("hide");
          this.planHeightInputBtnWrapper.classList.remove("hide");
          // 지정고도 입력폼에 포커스 추가.
          this._planHeight.focus();
        } else {
          this.planHeightInputPopup.classList.add("hide");
          this.planHeightInputBtnWrapper.classList.add("hide");
          await this.measurementVolume.calculateVolume({
            positions: this.measurementVolume.positions,
            planHeight: value,
            cellSize: this.measurementVolume.cellSize,
            showCell: this.measurementVolume.showCell,
            fillingVolumeColor: this.measurementVolume.fillingVolumeColor,
            cuttingVolumeColor: this.measurementVolume.cuttingVolumeColor,
            classification: this.measurementVolume.classification,
            loading: this.measurementVolume.loading,
            callback: this.measurementVolume.callback,
          });
          this.update();
        }
      });
    });

    // 지정 고도 검색 버튼
    this.planHeightSearch.addEventListener("click", async () => {
      const value = Number(this.planHeight.value);
      if (!isNaN(value)) {
        await this.measurementVolume.calculateVolume({
          positions: this.measurementVolume.positions,
          planHeight: value,
          cellSize: this.measurementVolume.cellSize,
          showCell: this.measurementVolume.showCell,
          fillingVolumeColor: this.measurementVolume.fillingVolumeColor,
          cuttingVolumeColor: this.measurementVolume.cuttingVolumeColor,
          classification: this.measurementVolume.classification,
          loading: this.measurementVolume.loading,
          callback: this.measurementVolume.callback,
        });
        this.update();
      }
    });

    // Measurement 엔티티 마우스 좌클릭 이벤트
    window.addEventListener("keydown", (e) => {
      this.handleKeyDown(e);
    });
  }

  createResizeEvent() {
    super.createResizeEvent();

    window.addEventListener(
      "resize",
      (this.setMobileEvent = () => {
        this.setMobilePopup();
      })
    );
  }

  removeResizeEvent() {
    super.removeResizeEvent();

    if (this.setMobileEvent) {
      window.removeEventListener("resize", this.setMobileEvent);
    }
  }

  /**
   * 팝업 닫기. </br>
   * Close popup.
   */
  close(element = undefined) {
    this.mobileHeightSelectPopup.close();
    super.close(element);
  }

  /**
   * 팝업 열기. </br>
   * Open popup.
   */
  open() {
    if (!this.measurementVolume?.isReady) {
      this.createWarningAlert.open();
    } else {
      this.close(this.planHeightList);
      this.close(this.planHeightInputPopup);
      this.update();

      this.createResizeEvent();

      if (this.measurementVolume.planHeightType === "user") {
        this.planHeightInputPopup.classList.remove("hide");
        this.planHeightInputBtnWrapper.classList.remove("hide");
      }

      // 부모 자식으로 wrapper 추가
      if (this.parent instanceof Element) {
        this.parent.append(this.wrapper);
      }

      this.wrapper.classList.remove("hide");
      this.ditapPaneManager?.addPane(this.wrapper);

      const mobileLabel = this.parent.getElementsByClassName(
        "ditap-mobile-label"
      );
      if (mobileLabel.length > 0) {
        Utils.moveUnderLabel(this.parent);
      }
    }
  }

  /**
   * 팝업 제거. </br>
   * Destroy popup.
   */
  destroy() {
    //this.handler.destroy();
    this.mobileHeightSelectPopup.destroy();
    super.destroy();
    //document.body.removeChild(this.wrapper);
  }

  // /**
  //  * 체적량 분석 엔티티 클릭 이벤트
  //  */
  // clickMeasurementVolume() {
  //   const viewer = this.viewer;
  //   const handler = this.handler;

  //   handler.setInputAction((e) => {
  //     // 좌표 추가
  //     const pickedModel = viewer.scene.pick(e.position);
  //     if (
  //       defined(pickedModel) &&
  //       pickedModel.id &&
  //       pickedModel.id instanceof Entity
  //     ) {
  //       const id = pickedModel.id.id;
  //       // if (id) {
  //       //   id = id.split("_")[0];
  //       // }

  //       const entity = viewer.ditapStorage.getInstanceById(id);

  //       if (entity && entity.id === this.measurementVolume.id) {
  //         // const volume = viewer.ditapStorage.measurementVolume[id];
  //         this.close(this.planHeightList);
  //         this.close(this.planHeightInputPopup);
  //         this.update();

  //         if (entity.planHeightType === "user") {
  //           this.open(this.planHeightInputPopup);
  //         }
  //         this.open();
  //       }
  //     }
  //   }, ScreenSpaceEventType.LEFT_CLICK);
  // }

  /*
   * 팝업 생성.<br>
   * Create popup
   * @returns {object} 생성된 팝업 속성 객체.<br> The attribute object of the generated popup element.
   * @example
   * {
      header: {
        title: 타이틀
      },
      content: {
        subTitle: 서브타이틀,
        mainWrapper: 컨텐츠 영역 Wrapper,
        planHeightWrapper: 계획고 Wrapper,
        detailWrapper: 상세정보 Wrapper,
        mainCuttingVolume: 메인 절토량,
        mainFillVolume: 메인 성토량,
        planHeightPopup: 계획고 Wrapper,
        planHeightSelectText: 선택한 계획고 타입,
        planHeightList: 계획고 목록 Wrapper,
        planHeightItem: [ - 계획고 목록 객체 리스트
          최저 기준,
          최고점 기준,
          평균점 기준,
          사용자 지정 고도,
        ],
        planHeightInputPopup: 계획고 Input Wrapper,
        planHeight: 계획고 Input,
        detailCuttingVolume: 상세정보 절토량,
        detailFillVolume: 상세정보 성토량,
        detailTotalVolume: 상세정보 총합,
      },
    }
  }
   */
  createInfoBox() {
    this.wrapper.classList.add(
      "ditap-popup-mobile",
      "ditap-volume-popup",
      "ditap-mobile-bottom",
      "header-top",
      "draggable"
    );
    // # 헤더
    const title = document.createElement("h2");
    title.classList.add("ditap-popup-title");
    title.innerText = "체적";

    // # 컨텐츠
    const content = document.createElement("div");
    content.classList.add("ditap-popup-content");

    const subTitle = document.createElement("p");
    subTitle.classList.add("ditap-popup-subtitle");
    subTitle.innerText = "절토량, 성토량을 확인하세요.";

    // 컨텐츠 > mainWrapper
    const mainWrapper = document.createElement("div");
    mainWrapper.classList.add("ditap-size-levels");
    const mainList = document.createElement("ul");
    mainList.classList.add("ditap-size-levels-list");
    const mainListItem1 = document.createElement("li");
    mainListItem1.classList.add("ditap-size-levels-item");
    const mainListItem1Title = document.createElement("p");
    mainListItem1Title.classList.add("ditap-size-levels-title");
    mainListItem1Title.innerText = "절토량";
    const mainListItem1Value = document.createElement("p");
    mainListItem1Value.classList.add("ditap-size-levels-value", "value-yellow");
    const mainListItem1ValueSpan = document.createElement("span");
    const mainListItem2 = document.createElement("li");
    mainListItem2.classList.add("ditap-size-levels-item");
    const mainListItem2Title = document.createElement("p");
    mainListItem2Title.innerText = "성토량";
    mainListItem2Title.classList.add("ditap-size-levels-title");
    const mainListItem2Value = document.createElement("p");
    mainListItem2Value.classList.add("ditap-size-levels-value", "value-red");
    const mainListItem2ValueSpan = document.createElement("span");
    // mainListItem1Value
    mainListItem1Value.innerText = "㎥";
    mainListItem1Value.prepend(mainListItem1ValueSpan);
    // mainListItem2Value
    mainListItem2Value.innerText = "㎥";
    mainListItem2Value.prepend(mainListItem2ValueSpan);
    // mainListItem1
    mainListItem1.append(mainListItem1Title, mainListItem1Value);
    // mainListItem2
    mainListItem2.append(mainListItem2Title, mainListItem2Value);
    // mainList
    mainList.append(mainListItem1, mainListItem2);
    // mainWrapper
    mainWrapper.append(mainList);

    const messageWrapper = document.createElement("div");
    messageWrapper.classList.add("ditap-popup-message", "hide");
    // const warningWrap = document.createElement("p");
    // warningWrap.classList.add("ditap-volume-popup-warning");
    // warningWrap.innerText = "!";
    const message = document.createElement("p");
    message.classList.add("ditap-volume-popup-message");
    const warning = document.createElement("p");
    warning.classList.add("ditap-volume-warning-img");
    messageWrapper.append(warning);
    message.innerText =
      "평면상에서 기준점과 드로잉 포인트의 높이가 같은 경우 계산 결과 없음";

    // messageWrapper.append(warningWrap);
    messageWrapper.append(message);

    // 컨텐츠 > planHeightWrapper
    const planHeightWrapper = document.createElement("div");
    planHeightWrapper.classList.add("ditap-popup-style-list");

    const planHeightItem1 = document.createElement("div");
    planHeightItem1.classList.add("ditap-style-item");
    const planHeightItem1Title = document.createElement("p");
    planHeightItem1Title.classList.add("ditap-style-title");
    planHeightItem1Title.innerText = "기준 밑면";
    const planHeightSelectWrapper = document.createElement("div");
    planHeightSelectWrapper.classList.add(
      "ditap-style-inp-wrap",
      "ditap-style-select"
    );

    const planHeightSelectedItem = document.createElement("div");
    planHeightSelectedItem.classList.add("ditap-style-inp");
    planHeightSelectedItem.innerText = "평군점 기준";
    const planHeightSelectDropImage = document.createElement("button");
    planHeightSelectDropImage.classList.add(
      "ditap-dropbox-img",
      "ditap-custom-button"
    );
    const planHeightSelectListWrapper = document.createElement("div");
    planHeightSelectListWrapper.classList.add("ditap-style-opt", "hide");
    const planHeightSelectList = document.createElement("ul");
    planHeightSelectList.classList.add("ditap-style-opt-list");
    const planHeightSelectItem1 = document.createElement("button");
    planHeightSelectItem1.classList.add(
      "ditap-style-opt-title",
      "ditap-custom-button"
    );
    planHeightSelectItem1.innerText = "최저점 기준";
    planHeightSelectItem1.setAttribute("value", "min");
    const planHeightSelectItem2 = document.createElement("button");
    planHeightSelectItem2.classList.add(
      "ditap-style-opt-title",
      "ditap-custom-button"
    );
    planHeightSelectItem2.innerText = "최고점 기준";
    planHeightSelectItem2.setAttribute("value", "max");
    const planHeightSelectItem3 = document.createElement("button");
    planHeightSelectItem3.classList.add(
      "ditap-style-opt-title",
      "ditap-custom-button"
    );
    planHeightSelectItem3.innerText = "평균점 기준";
    planHeightSelectItem3.setAttribute("value", "average");
    const planHeightSelectItem4 = document.createElement("button");
    planHeightSelectItem4.classList.add(
      "ditap-style-opt-title",
      "ditap-custom-button"
    );
    planHeightSelectItem4.innerText = "사용자 지정 고도";
    planHeightSelectItem4.setAttribute("value", "user");

    const planHeightItem2 = document.createElement("div");
    planHeightItem2.classList.add("ditap-style-item", "user-select");
    const planHeightItem2Title = document.createElement("p");
    planHeightItem2Title.classList.add("ditap-style-title");
    planHeightItem2Title.innerText = "지정 고도";
    const planHeightInputWrapper = document.createElement("div");
    planHeightInputWrapper.classList.add("ditap-style-inp-wrap");
    const planHeightInputLabel = document.createElement("label");
    const planHeightInput = document.createElement("input");
    planHeightInput.classList.add("ditap-style-inp");

    const planeHeightSearchBtnWrapper = document.createElement("div");
    planeHeightSearchBtnWrapper.classList.add("ditap-btn-wrap", "hide");
    const planHeightSearch = document.createElement("button");
    planHeightSearch.classList.add("ditap-popup-btn", "gold");
    planHeightSearch.innerText = "계산하기";
    planeHeightSearchBtnWrapper.append(planHeightSearch);
    // const planHeightSearchLabel = document.createElement("label");
    // const planHeightSearch = document.createElement("input");
    // planHeightSearch.setAttribute("type", "button");
    // planHeightSearch.setAttribute("name", "search");
    // planHeightSearch.classList.add(
    //   "btn-reset",
    //   "ditap-search-find",
    //   "js-ditap-search-btn"
    // );

    planHeightInput.addEventListener("input", (event) => {
      const result = this.checkRegularExpression(event.target.value);
      if (!result) {
        planHeightInput.value = event.target.value.slice(0, -1);
      }
    });

    // planHeightSelectList
    planHeightSelectList.append(
      planHeightSelectItem1,
      planHeightSelectItem2,
      planHeightSelectItem3,
      planHeightSelectItem4
    );
    // planHeightSelectListWrapper
    planHeightSelectListWrapper.append(planHeightSelectList);
    // planHeightSelectWrapper
    planHeightSelectWrapper.append(
      planHeightSelectedItem,
      planHeightSelectDropImage,
      planHeightSelectListWrapper
    );
    // planHeightItem1
    planHeightItem1.append(planHeightItem1Title, planHeightSelectWrapper);
    // planHeightInputLabel
    planHeightInputLabel.append(planHeightInput);
    // planHeightSearchLabel
    // planHeightSearchLabel.append(planHeightSearch);
    // planHeightInputWrapper
    planHeightInputWrapper.append(
      planHeightInputLabel
      // planeHeightSearchBtnWrapper
    );
    // planHeightItem2
    planHeightItem2.append(planHeightItem2Title, planHeightInputWrapper);
    // planHeightWrapper
    planHeightWrapper.append(
      planHeightItem1,
      planHeightItem2,
      planeHeightSearchBtnWrapper
    );

    // 컨텐츠 > detail
    const detailWrapper = document.createElement("div");
    detailWrapper.classList.add("ditap-popup-property");
    const detailWrapperTitle = document.createElement("p");
    detailWrapperTitle.classList.add("ditap-volume-detail-title");
    detailWrapperTitle.innerText = "상세정보";
    const detailList = document.createElement("ul");
    detailList.classList.add("ditap-popup-property-list");
    const detailItem1 = document.createElement("li");
    detailItem1.classList.add("ditap-property-item", "property-red-dot");
    const detailItem1Title = document.createElement("p");
    detailItem1Title.classList.add("ditap-property-key");
    detailItem1Title.innerText = "절토량";
    const detailItem1Value = document.createElement("p");
    detailItem1Value.classList.add("ditap-property-value");
    const detailItem1ValueSpan = document.createElement("span");
    const detailItem2 = document.createElement("li");
    detailItem2.classList.add("ditap-property-item", "property-yellow-dot");
    const detailItem2Title = document.createElement("p");
    detailItem2Title.classList.add("ditap-property-key");
    detailItem2Title.innerText = "성토량";
    const detailItem2Value = document.createElement("p");
    detailItem2Value.classList.add("ditap-property-value");
    const detailItem2ValueSpan = document.createElement("span");
    const detailItem3 = document.createElement("li");
    detailItem3.classList.add("ditap-property-item", "property-green-dot");
    const detailItem3Title = document.createElement("p");
    detailItem3Title.classList.add("ditap-property-key");
    detailItem3Title.innerText = "절토/성토량 총합";
    const detailItem3Value = document.createElement("p");
    detailItem3Value.classList.add("ditap-property-value");
    const detailItem3ValueSpan = document.createElement("span");
    // detailItem1Value
    detailItem1Value.innerText = "㎥";
    detailItem1Value.prepend(detailItem1ValueSpan);
    // detailItem1
    detailItem1.append(detailItem1Title, detailItem1Value);
    // detailItem2Value
    detailItem2Value.innerText = "㎥";
    detailItem2Value.prepend(detailItem2ValueSpan);
    // detailItem2
    detailItem2.append(detailItem2Title, detailItem2Value);
    // detailItem3Value
    detailItem3Value.innerText = "㎥";
    detailItem3Value.prepend(detailItem3ValueSpan);
    // detailItem1
    detailItem3.append(detailItem3Title, detailItem3Value);
    // detailList
    detailList.append(detailItem1, detailItem2, detailItem3);
    // detailWrapper
    detailWrapper.append(detailWrapperTitle, detailList);

    // const headerHTML = `
    //   <div class="ditap-popup-header">
    //     <h2 class="ditap-popup-title">체적</h2> -- title
    //     <button class="ditap-popup-close-btn"></button> -- closeBtn
    //   </div>
    // `;
    // const contentHTML = `
    //   <div class="ditap-popup-content">
    //     <p class="ditap-popup-subtitle">절토량, 성토량을 확인하세요.</p> -- subTitle
    //     <div class="ditap-size-levels"> -- mainWrapper
    //       <ul class="ditap-size-levels-list"> -- mainList
    //         <li class="ditap-size-levels-item"> -- mainListItem1
    //           <p class="ditap-size-levels-title">절토량</p> -- mainListItem1Title
    //           <p class="ditap-size-levels-value value-red"><span></span>&nbsp;㎥</p> -- mainListItem1Value
    //         </li>
    //         <li class="ditap-size-levels-item"> -- mainListItem2
    //           <p class="ditap-size-levels-title">성토량</p> -- mainListItem2Title
    //           <p class="ditap-size-levels-value value-yellow"><span></span>&nbsp;㎥</p> -- mainListItem2Value
    //         </li>
    //       </ul>
    //     </div>

    //     <div class="ditap-popup-style-list"> -- planHeightWrapper
    //       <div class="ditap-style-item"> -- planHeightItem1
    //         <p class="ditap-style-title">기준 밑면</p> -- planHeightItem1Title
    //         <div class="ditap-style-inp-wrap ditap-style-select"> -- planHeightSelectWrapper
    //           <div class="ditap-style-inp">평균점 기준</div>  -- planHeightSelectedItem
    //           <p class="ditap-dropbox-img"></p> -- planHeightSelectDropImage
    //           <div class="ditap-style-opt hide"> -- planHeightSelectListWrapper
    //             <ul class="ditap-style-opt-list"> -- planHeightSelectList
    //               <li class="ditap-style-opt-title" value="min">최저점 기준</li> -- planHeightSelectItem1
    //               <li class="ditap-style-opt-title" value="max">최고점 기준</li> -- planHeightSelectItem2
    //               <li class="ditap-style-opt-title" value="average">평균점 기준</li> -- planHeightSelectItem3
    //               <li class="ditap-style-opt-title" value="user">사용자 지정 고도</li> -- planHeightSelectItem4
    //             </ul>
    //           </div>
    //         </div>
    //       </div>
    //       <div class="ditap-style-item hide"> -- planHeightItem2
    //         <p class="ditap-style-title">지정 고도</p> -- planHeightItem2Title
    //         <div class="ditap-style-inp-wrap"> -- planHeightInputWrapper
    //           <label> -- planHeightInputLabel
    //             <input type="text" class="ditap-style-inp" value="" /> -- planHeightInput
    //           </label>
    //         </div>
    //       </div>
    //     </div>

    //     <div class="ditap-popup-property"> -- detailWrapper
    //       <p class="ditap-popup-subtitle">상세정보를 확인하세요.</p> -- detailWrapperTitle
    //       <ul class="ditap-popup-property-list"> -- detailList
    //         <li class="ditap-property-item property-red-dot"> -- detailItem1
    //           <p class="ditap-property-key">절토량</p> -- detailItem1Title
    //           <p class="ditap-property-value value-red"><span></span>&nbsp;㎥</p> -- detailItem1Value
    //         </li>
    //         <li class="ditap-property-item property-yellow-dot"> -- detailItem2
    //           <p class="ditap-property-key">성토량</p> -- detailItem2Title
    //           <p class="ditap-property-value value-yellow"><span></span>&nbsp;㎥</p> -- detailItem2Value
    //         </li>
    //         <li class="ditap-property-item property-green-dot"> -- detailItem2
    //           <p class="ditap-property-key">절토/성토량 총합</p> -- detailItem2Title
    //           <p class="ditap-property-value"><span></span>&nbsp;㎥</p> -- detailItem3Value
    //         </li>
    //       </ul>
    //     </div>
    //   </div>
    // `;

    return {
      header: {
        title: title,
      },
      content: {
        mainWrapper: mainWrapper,
        messageWrapper: messageWrapper,
        planHeightWrapper: planHeightWrapper,
        detailWrapper: detailWrapper,
        subTitle: subTitle,
        mainCuttingVolume: mainListItem1ValueSpan,
        mainFillingVolume: mainListItem2ValueSpan,
        planHeightPopup: planHeightSelectWrapper,
        planHeightSelectDropImage: planHeightSelectDropImage,
        planHeightSelectText: planHeightSelectedItem,
        planHeightList: planHeightSelectListWrapper,
        planHeightItem: [
          planHeightSelectItem1,
          planHeightSelectItem2,
          planHeightSelectItem3,
          planHeightSelectItem4,
        ],
        planHeightInputPopup: planHeightItem2,
        planHeightInputBtnWrapper: planeHeightSearchBtnWrapper,
        planHeight: planHeightInput,
        planHeightSearch: planHeightSearch,
        detailCuttingVolume: detailItem1ValueSpan,
        detailFillingVolume: detailItem2ValueSpan,
        detailTotalVolume: detailItem3ValueSpan,
      },
    };
  }
  /*
   * 입력된 값 실수인지 확인. </br>
   * Determine if the value entered as a parameter is a real number.
   * @param {string} value 확인 하려는 값. </br> Value want to check
   * @returns {boolean} 실수 여부. </br> Real number status
   */
  checkRegularExpression(value) {
    const pattern = /^[+-]?\d*(\.?\d*)$/;
    if (pattern.test(value)) {
      return true;
    }
    return false;
  }
}

export default PopupMeasurementVolume;
